/* eslint-disable import/no-cycle */
import { get, compact, uniq, groupBy } from 'lodash';
import { retrieveObjectMapper } from 'utils';
import { DEFAULT_ERROR } from '../utils/constants';
import DatadogHandler from '../utils/datadog';
import {
  childService,
  financeService,
  MS_PHASE_3_ENDPOINTS,
} from '../utils/msEndpointConstants';
import { getChildByIds } from './ChildService';
import { checkTransactionInstanceStatus } from './RegistrationsService';
import {
  transformBankBicCodes,
  transformChildBankAccounts,
  transformBankDocumentTypes,
  transformAccountHistory,
  transformBankAccountStatusChangeReasons,
} from '../utils/ms-utils/ms-phase-3/BankAccountUtils';

const { BANK_ACCOUNT } = MS_PHASE_3_ENDPOINTS;

const fileName = 'src/services/BankAccountService.js';

export const msGetBankBicCodeByIds = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...BANK_ACCOUNT.getBankBicCodeByIds(reqData)
    );

    const bicCodes = resp?.data?.data ?? [];
    const data = transformBankBicCodes(bicCodes);
    return { success: true, data };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetBankBicCodeByIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetBankDocumentByIds = async (_, reqData) => {
  try {
    const childSvc = childService();
    const resp = await childSvc.post(
      ...BANK_ACCOUNT.getBankDocumentByAccountIds(reqData)
    );

    const data = resp?.data?.document_types || [];
    const transformedData = transformBankDocumentTypes(data || []);
    return { success: true, data: transformedData };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetBankDocumentByIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAccountStateHistoryByAccountIds = async (
  _,
  { bankAccountIds }
) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...BANK_ACCOUNT.getAccountStateHistoryByAccountIds({ bankAccountIds })
    );

    const data = resp?.data?.data || [];
    const transform = transformAccountHistory(data);
    const accountHistories = groupBy(transform || [], 'fkBankAccount');
    return { success: true, bankAccountStateHistories: accountHistories };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAccountStateHistoryByAccountIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetChildBankAccountDetails = async (bankAccounts = []) => {
  const fkBankBicCodes = compact(
    uniq(
      bankAccounts.map(
        ({ bank_account: bankAccount }) => bankAccount?.fk_bank_bic_code
      )
    )
  );
  const fkChildIds = compact(
    uniq(
      bankAccounts.map(({ bank_account: bankAccount }) => bankAccount?.fk_child)
    )
  );
  const bankAccountIds = compact(
    uniq(bankAccounts.map(({ bank_account: bankAccount }) => bankAccount?.id))
  );

  const [getBankBicCodeResp, bankDocs, stateHistory, childByIds] =
    await Promise.all([
      msGetBankBicCodeByIds(null, fkBankBicCodes),
      msGetBankDocumentByIds(null, bankAccountIds),
      msGetAccountStateHistoryByAccountIds(null, { bankAccountIds }),
      getChildByIds(fkChildIds),
    ]);

  const data = transformChildBankAccounts({
    data: bankAccounts,
    bankBicCodes: getBankBicCodeResp?.data || [],
    accountStateHistory: stateHistory?.bankAccountStateHistories || {},
    bankDocs: bankDocs?.data || {},
    childList: childByIds,
  });
  return data;
};

export const msGetChildBankAccounts = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...BANK_ACCOUNT.bankAccountInfoByIDChild(reqData)
    );

    const bankAccounts = resp?.data?.data ?? [];
    const data = await msGetChildBankAccountDetails(bankAccounts);

    return {
      success: true,
      data,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetChildBankAccounts',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getBankBicCodeByIds = async (bankBicCodeIds = []) => {
  if (bankBicCodeIds.length === 0) return Promise.resolve([]);

  return financeService()
    .post(...BANK_ACCOUNT.getBankBicCodeByIds(bankBicCodeIds))
    .then(r => r.data)
    .catch(() => ({ data: [], totalCount: 0 }));
};

export const getBankAccountStateHistoryByAccountIds = async (
  bankAccountIds = []
) => {
  if (bankAccountIds.length === 0) return Promise.resolve([]);

  return financeService()
    .post(
      ...BANK_ACCOUNT.getAccountStateHistoryByAccountIds({ bankAccountIds })
    )
    .then(r => r.data)
    .catch(() => ({ data: [], totalCount: 0 }));
};

export const msCheckBankAccountForPreEnrolmentWithdrawal = async (
  _,
  { IDChild }
) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...BANK_ACCOUNT.checkPreWnrolmentWithdrawal({ IDChild })
    );

    const allowed = resp?.data?.allowed || false;
    return {
      success: true,
      data: {
        checkBankAccountForPreEnrolmentWithdrawal: allowed,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCheckBankAccountForPreEnrolmentWithdrawal',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getAllBankBicCode = async filters => {
  return financeService()
    .get(...BANK_ACCOUNT.getAllBankBicCode(filters))
    .then(res => ({
      data: get(res, 'data.data', []),
      totalCount: get(res, 'data.totalCount', 0),
    }))
    .catch(() => ({ data: [], totalCount: 0 }));
};

export const msUpdateBankAccountBillRefNumber = async (_, reqBody) => {
  try {
    const resp = await financeService().post(
      ...BANK_ACCOUNT.updateBankAccountBillRefNumber(reqBody)
    );

    const responseData = get(resp, 'data', {});
    const transactionInstanceId = get(responseData, 'transaction_instance_id');

    const isTransactionCompleted = await checkTransactionInstanceStatus(
      transactionInstanceId,
      responseData,
      'msUpdateBankAccountBillRefNumber'
    );

    return {
      success: true,
      data: {
        updateBankAccountBillReferenceNumber: {
          ID: get(resp, 'data.id'),
          billReferenceNumber: get(resp, 'data.bill_reference_number'),
          isTransactionCompleted,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpdateBankAccountBillRefNumber',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetBankAccountStatusChangeReasonsByStatus = async (
  _,
  reqBody
) => {
  try {
    const resp = await financeService()
      .post(...BANK_ACCOUNT.getBankAccountStatusChangeReasons(reqBody))
      .then(r => ({
        data: get(r, 'data.data', []),
        totalCount: get(r, 'data.totalCount', 0),
      }))
      .catch(() => ({ data: [], totalCount: 0 }));
    return {
      data: transformBankAccountStatusChangeReasons(resp),
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetBankAccountStatusChangeReasonsByStatus',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateBankAccountStatus = async (_, reqBody) => {
  try {
    const resp = await financeService().post(
      ...BANK_ACCOUNT.updateBankAccountStatus(reqBody)
    );
    const bankBicCodeId = get(resp, 'data.fk_bank_bic_code');
    const bankBicCodeResp = await getBankBicCodeByIds([bankBicCodeId]);
    const bankBicCode = get(bankBicCodeResp, 'data[0]');
    return {
      success: true,
      data: {
        updateBankAccountStatus: {
          ...retrieveObjectMapper(resp?.data, [
            'ID:id',
            'payer_account_number',
            'payer_account_name',
            'source:source_string',
            'status:status_string',
          ]),
          bankBicCode: bankBicCode
            ? retrieveObjectMapper(bankBicCode, ['ID:id', 'bank_name'])
            : null,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpdateBankAccountStatus',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msAddBankBankAccountForRefund = async (_, reqData) => {
  try {
    const resp = await financeService().post(
      BANK_ACCOUNT.addBankAccountForRefund,
      {
        application: {
          application_date: get(reqData, 'application.ApplicationDate'),
          bank_account_number: get(reqData, 'application.BankAccountNumber'),
          bank_id: get(reqData, 'application.BankID'),
          ...(get(reqData, 'application.ChildName') && {
            child_name: get(reqData, 'application.ChildName'),
          }),
          ...(get(reqData, 'application.ChildBirthCertificate') && {
            child_birth_certificate: get(
              reqData,
              'application.ChildBirthCertificate'
            ),
          }),
          is_cda: get(reqData, 'application.IsCDA'),
          mobile_phone_country_code: get(
            reqData,
            'application.MobilePhoneCountryCode'
          ),
          mobile_phone_number: get(reqData, 'application.MobilePhoneNumber'),
          name_of_account_holders:
            get(reqData, 'application.NameOfAccountHolders') || [],
        },
        ...(get(reqData, 'IDChild') && { id_child: get(reqData, 'IDChild') }),
        ...(get(reqData, 'IDRegChild') && {
          id_reg_child: get(reqData, 'IDRegChild'),
        }),
        ...(get(reqData, 'IDECDARegChild') && {
          id_ecda_reg_child: get(reqData, 'IDECDARegChild'),
        }),
        is_sibling_cda: get(reqData, 'isSiblingCda'),
      }
    );

    return {
      success: true,
      data: {
        addBankAccountForRefund: {
          ID: get(resp, 'data.bank_account.id'),
          documentTypes: {
            data: [
              {
                ...retrieveObjectMapper(get(resp, 'data.document_type'), [
                  'ID:id',
                ]),
                documentTypeConfig: retrieveObjectMapper(
                  get(resp, 'data.document_type_config'),
                  ['ID:id', 'label', 'code']
                ),
              },
            ],
          },
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddBackBankAccountForRefund',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getBankAccountsByChildIds = (childIds = [], params = {}) => {
  if (childIds?.length === 0) {
    return [];
  }
  return financeService()
    .post(...BANK_ACCOUNT.getByChildIds(childIds, params))
    .then(r => get(r, 'data', {}))
    .catch(() => ({}));
};
